import React, { useEffect, useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { FaCheckCircle } from 'react-icons/fa';
import PersonIcon from '@mui/icons-material/Person';
import HomeFilledIcon from '@mui/icons-material/HomeFilled';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';

import LocationPinIcon from '@mui/icons-material/LocationPin';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import { AppColors, typographyClamps } from '../utils/utils';
import Page from '../components/Page';
import { HtmlRenderer } from '../components/HtmlRenderer';
import { BlueGradientButton } from '../components/BlueGradientButton';
import { useFormik } from 'formik';
import {
  campaignLeadSchema,
  campaignLeadQuestionsSchema,
} from '../validation/campaignLeadSchema';
import { getAssetURL, getCapitalizedString } from '../utils/utilMethods';
import {
  formatDateMMDDYYYY,
  formatTimeHHmm,
} from '../utils/formatTime';
import { useAppDispatch } from '../store';
import {
  createCampaignLead,
  getCampaign,
  updateCampaignLead,
} from '../store/nfadaCampaign/api';
import {
  Campaign,
  CampaignLead,
  CreateCampaignLead,
  UpdateCampaignLead,
} from '../store/nfadaCampaign/api/interface';
import { useParams } from 'react-router-dom';
import Loader from '../components/Loader';

// Types
type EntryStep = 'gate' | 'entry-form' | 'confirmation';

const entrySteps = [
  `Complete the official <span>digital entry form</span>`,
  `Receive <span>instant confirmation by text</span>`,
  `Show confirmation to receive your <span>Bisons vouchers</span>`,
];

//const confirmationImageSrc = '/static/nfada_campaign_page/nfada_confirmation.png';

const HEAR_ABOUT_OPTIONS = [
  'TV',
  'Radio',
  'Website',
  'Social Media',
  'Other',
];

const PURCHASE_PLAN_OPTIONS = ['Yes', 'No'];

const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

const NFADACampaign: React.FC = () => {
  const dispatch = useAppDispatch();

  const params: any = useParams();
  const campaignCode = params?.code;

  const [step, setStep] = useState<EntryStep>('gate');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px

  const [isFetchingCampaign, setIsFetchingCampaign] = useState<boolean>(false);
  const [campaignData, setCampaignData] = useState<Campaign | undefined>(
    undefined,
  );
  const [campaignLeadData, setCampaignLeadData] = useState<
    CampaignLead | undefined
  >(undefined);
  const [isSubmittingQuestions, setIsSubmittingQuestions] =
    useState<boolean>(false);
  const [questionsSubmitted, setQuestionsSubmitted] = useState<boolean>(false);

  const px = { xs: '5%', sm: '10%' };

  const iconFontSize = 'small';

  const inputFieldLabelStyle = {
    color: AppColors.blackColor,
    fontWeight: 600,
    fontSize: isMobile ? '12px' : '18px',
    mb: 0.5,
    '& span': {
      color: '#B91C1C',
    },
  };

  const textFieldStyle = {
    '& label': {
      color: AppColors.grayThreeColor,
    },
    '& .MuiOutlinedInput-root input': {
      color: AppColors.blackColor,
      paddingTop: '8px', // Reduced top padding
      paddingBottom: '8px', // Reduced bottom padding
    },
    // 👉 Placeholder styling
    '& .MuiOutlinedInput-root input::placeholder': {
      fontSize: '12px', // placeholder font size
      color: AppColors.grayThreeColor,
      opacity: 1, // important for Firefox
    },
    // For Select component - reduce padding
    '& .MuiSelect-select': {
      paddingTop: '8px', // Reduced top padding for Select
      paddingBottom: '8px', // Reduced bottom padding for Select
    },
    '& .MuiOutlinedInput-root': {
      // borderRadius: '8px', // Added border radius
      // background: AppColors.grayZeroColor,
      '& fieldset': {
        borderColor: AppColors.grayOneColor,
        // borderRadius: '8px', // Added border radius to fieldset
      },
      '&:hover fieldset': {
        borderColor: AppColors.grayOneColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: AppColors.grayOneColor,
      },
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: ` ${AppColors.grayOneColor} !important`,
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: `${AppColors.primaryColor} !important`,
    },
    '& .MuiInputLabel-shrink': {
      backgroundColor: AppColors.grayZeroColor,
      paddingLeft: '5px',
      paddingRight: '5px',
      borderRadius: '5px',
    },
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' }); // Or use 'smooth' for smooth scrolling
  }, [step]);


  useEffect(() => {
    const fetchByCode = async () => {
      if (!campaignCode) return;

      try {
        setIsFetchingCampaign(true);
        const result = await dispatch(
          getCampaign({ code: campaignCode }),
        ).unwrap();

        setCampaignData(result);
      } catch (error: any) {
      } finally {
        setIsFetchingCampaign(false);
      }
    };

    fetchByCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignCode]);

  const {
    handleSubmit,
    values,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormik<CreateCampaignLead>({
    initialValues: {
      firstName: '',
      lastName: '',
      address: '',
      email: '',
      mobileNumber: '',
      city: '',
      state: '',
      zipCode: '',
      dateOfBirth: '',
      driverLicense: '',
    },
    validationSchema: campaignLeadSchema,
    onSubmit: async (values) => {
      try {
        const response = await dispatch(
          createCampaignLead({ ...values, campaignId: campaignData?.id }),
        ).unwrap();

        // showSuccessMessage(
        //   `Thanks for your interest. An agent will be in touch with you shortly.`,
        // );

        if (response && response.campaignLeadId) {
          setCampaignLeadData(response);
          setStep('confirmation');
          resetForm();
        }
      } catch (error: any) {
        console.error(error);
      }
    },
  });

  const renderFooter = () => (
    <Box sx={{ px: px, py: 1, mt: 2, bgcolor: AppColors.grayZeroColor }}>
      <Typography
        sx={{ fontSize: typographyClamps.caption, textAlign: 'center' }}
      >
        The winner is responsible for all taxes and tags. Must be 18 years of
        age or older.
        <br />
        This digital entry form is the official electronic Entry Blank
        equivalent to the NFADA paper ballot.
      </Typography>
    </Box>
  );

  const renderGateStep = () => (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundImage: `url('/static/nfada_campaign_page/nfada_banner.png')`,
          backgroundPosition: 'top',
          backgroundSize: '100% auto',
          backgroundRepeat: 'no-repeat',
          gap: 1.5,
          px: px,
        }}
      >
        <Box
          component={'img'}
          src='/static/nfada_campaign_page/nfada_logo.png'
          sx={{
            maxHeight: '10vh',
            // width: 'auto',
            // mx: 'auto',
            bgcolor: '#fff',
            borderRadius: 20,
          }}
        />

        <Typography
          variant='h4'
          sx={{
            // fontSize: typographyClamps.h4,
            fontWeight: 500,
            color: 'black',
            textAlign: 'center',
            '& span': {
              color: '#135395',
              fontWeight: 800,
            },
          }}
        >
          Chance to Win a <span>CAR A DAY</span>
        </Typography>

        <Typography
          variant='h6'
          sx={{
            // fontSize: typographyClamps.h6,
            fontWeight: 500,
            color: AppColors.autoShowTextColor,
            textAlign: 'center',
          }}
        >
          2026 Buffalo Auto Show Official Promotion
        </Typography>

        <Typography
          variant='body1'
          sx={{
            textAlign: 'center',
            color: AppColors.autoShowTextColor,
            '& span': {
              fontWeight: 600,
            },
          }}
        >
          Enter today for your chance to{' '}
          <span>win the featured Vehicle of the Day — </span>
          plus receive{' '}
          <span>2 FREE Buffalo Bisons Baseball Ticket Vouchers</span> when you
          complete your entry.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          mt: 3,
          backgroundImage: `url('/static/nfada_campaign_page/nfada_banner.png')`,
          backgroundPosition: 'center',
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          px: px,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 2,
          }}
        >
          <Divider sx={{ flex: 1, borderColor: AppColors.strokeColor }} />

          <Typography
            variant='h6'
            sx={{ color: AppColors.autoShowTextColor, whiteSpace: 'nowrap' }}
          >
            Today’s Vehicle of the Day:
          </Typography>
          <Divider sx={{ flex: 1, borderColor: AppColors.strokeColor }} />
        </Box>
        <Typography
          variant='h6'
          sx={{ color: AppColors.autoShowTextColor, fontWeight: 700 }}
        >
          {campaignData?.carTitle}
        </Typography>


        {campaignData?.photos && campaignData?.photos?.length > 0 && (<Box
          component={'img'}
          src={getAssetURL(campaignData?.photos[0].key, campaignData?.photos[0].url)}
          alt={campaignData.title}
          sx={{ maxWidth: '90%', height: 'auto', mt: 1 }}
        />)}

      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: px,
          mt: 1,
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            rowGap: 0.5,
            fontSize: isMobile ? '15px' : '18px',
          }}
        >
          {entrySteps.map((item, index) => {
            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  // alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    color: AppColors.autoShowGreenColor,
                    fontSize: 20,
                    // fontSize: 'inherit',
                    display: 'flex',
                    flexShrink: 0,
                    // mt: -1,
                  }}
                >
                  {FaCheckCircle({}) as React.ReactElement}
                </Box>
                <Typography
                  sx={{
                    fontSize: 'inherit',
                    color: AppColors.autoShowGreenColor,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Step {index + 1}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: 'inherit',
                    '& span': {
                      fontWeight: 600,
                    },
                  }}
                >
                  <HtmlRenderer htmlString={item} />
                </Typography>
              </Box>
            );
          })}
        </Box>
        <BlueGradientButton
          sx={{
            minHeight: '42px',
            height: 'auto !important',
          }}
          onClick={() => {
            setStep('entry-form');
          }}
        >
          START MY OFFICIAL ENTRY
        </BlueGradientButton>

        <Stack
          direction={'row'}
          spacing={1}
          sx={{
            fontSize: isMobile ? '12px' : '16px',
            fontWeight: 700,
            color: AppColors.autoShowFooterTextColor,
          }}
        >
          <Typography fontSize={'inherit'} fontWeight={'inherit'}>
            Contest Rules
          </Typography>

          <Typography variant='body2'>|</Typography>

          <Typography fontSize={'inherit'} fontWeight={'inherit'}>
            Must be 18+ to enter
          </Typography>
        </Stack>
      </Box>
    </>
  );

  const renderEntryFormStep = () => (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundImage: `url('/static/nfada_campaign_page/nfada_banner.png')`,
          backgroundPosition: 'top',
          backgroundSize: '100% auto',
          backgroundRepeat: 'no-repeat',
          gap: 1.5,
          px: px,
        }}
      >
        <Box
          component={'img'}
          src='/static/nfada_campaign_page/nfada_logo.png'
          sx={{
            maxHeight: '10vh',
            width: 'auto',
            // mx: 'auto',
            // bgcolor: '#fff',
            // borderRadius: 20,
          }}
        />

        <Typography
          variant='h5'
          sx={{
            fontWeight: 500,
            color: AppColors.autoShowTextColor,
            textAlign: 'center',
            '& span': {
              fontWeight: 800,
            },
          }}
        >
          OFFICIAL <span>ENTRY FORM</span>
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ px: px, mt: 1 }}>
        <Grid size={8} sx={{ color: AppColors.autoShowTextColor }}>
          <Typography
            variant='body1'
            sx={{ fontWeight: 700, textAlign: 'end' }}
          >
            Chance to Win a CAR-A-DAY!
          </Typography>
          <Typography
            sx={{
              fontSize: isMobile ? '12px' : '16px',
              fontWeight: 400,
              textAlign: 'center',
            }}
          >
            Please complete the form below to enter for a chance to win{' '}
            <strong>Today’s Vehicle of the Day —</strong>
          </Typography>
        </Grid>
        <Grid size={4}>
          <Box
            sx={{
              backgroundImage: `url('/static/nfada_campaign_page/nfada_banner.png')`,
              backgroundPosition: 'center',
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
            }}
          >


            {campaignData?.photos && campaignData?.photos?.length > 0 && (<Box
              component={'img'}
              src={getAssetURL(campaignData?.photos[0].key, campaignData?.photos[0].url)}
              alt={campaignData.title}
              sx={{ maxWidth: '100%', height: 'auto', mt: 1 }}
            />)}

          </Box>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: px,
        }}
      >
        <Divider
          sx={{
            flex: 1,
            borderColor: AppColors.strokeColor,
            mt: -2.5,
          }}
        />
        <Box>
          <Typography
            sx={{
              fontSize: isMobile ? '12px' : '16px',
              color: AppColors.autoShowTextColor,
              whiteSpace: 'nowrap',
            }}
          >
            Entering for today’s vehicle:
          </Typography>

          <Typography
            variant='body2'
            sx={{
              color: AppColors.autoShowTextColor,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              textAlign: 'center',
            }}
          >
            {campaignData?.carTitle}
          </Typography>
        </Box>
      </Box>

      {/* Entry form */}
      <form onSubmit={handleSubmit} autoComplete='off'>
        <Stack direction='column' spacing={2} sx={{ px: px, mt: 1 }}>
          <Grid container spacing={2} justifyContent={'center'}>
            <Grid size={6}>
              <Stack direction={'row'} gap={0.5}>
                <PersonIcon
                  fontSize={iconFontSize}
                  sx={{ color: AppColors.autoShowGreenColor }}
                />
                <Typography sx={inputFieldLabelStyle}>
                  First Name <span>*</span>
                </Typography>
              </Stack>
              <TextField
                id='firstName'
                name='firstName'
                placeholder='First Name'
                fullWidth
                onChange={handleChange}
                value={values.firstName}
                onInput={(e: any) => {
                  e.target.value = getCapitalizedString(e.target.value ?? '');
                }}
                error={Boolean(errors.firstName && touched.firstName)}
                helperText={
                  Boolean(errors.firstName && touched.firstName) &&
                  errors.firstName
                }
                sx={{ ...textFieldStyle }}
                inputProps={{
                  maxLength: 50,
                }}
              />
            </Grid>
            <Grid size={6}>
              <Typography sx={inputFieldLabelStyle}>
                Last Name <span>*</span>
              </Typography>
              <TextField
                id='lastName'
                name='lastName'
                placeholder='Last Name'
                fullWidth
                onChange={handleChange}
                value={values.lastName}
                onInput={(e: any) => {
                  e.target.value = getCapitalizedString(e.target.value ?? '');
                }}
                error={Boolean(errors.lastName && touched.lastName)}
                helperText={
                  Boolean(errors.lastName && touched.lastName) &&
                  errors.lastName
                }
                sx={{ ...textFieldStyle }}
                inputProps={{
                  maxLength: 50,
                }}
              />
            </Grid>
          </Grid>

          <Box width={'100%'}>
            <Stack direction={'row'} gap={0.5}>
              <HomeFilledIcon
                fontSize={iconFontSize}
                sx={{ color: AppColors.autoShowGreenColor }}
              />
              <Typography sx={inputFieldLabelStyle}>
                Address <span>*</span>
              </Typography>
            </Stack>
            <TextField
              id='address'
              name='address'
              placeholder='Street Address'
              fullWidth
              onChange={handleChange}
              value={values.address}
              onInput={(e: any) => {
                e.target.value = getCapitalizedString(e.target.value ?? '');
              }}
              error={Boolean(errors.address && touched.address)}
              helperText={
                Boolean(errors.address && touched.address) && errors.address
              }
              sx={{ ...textFieldStyle }}
              inputProps={{
                maxLength: 200,
              }}
            />
          </Box>

          <Grid container spacing={1}>
            <Grid size={5.5}>
              <TextField
                fullWidth
                placeholder='City'
                name='city'
                value={values.city}
                onChange={handleChange}
                onBlur={handleBlur}
                onInput={(e: any) => {
                  e.target.value = getCapitalizedString(e.target.value ?? '');
                }}
                error={Boolean(errors.city && Boolean(errors.city))}
                helperText={Boolean(errors.city && touched.city) && errors.city}
                sx={{ ...textFieldStyle }}
                inputProps={{
                  maxLength: 50,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <LocationPinIcon
                        fontSize={iconFontSize}
                        sx={{ color: AppColors.autoShowGreenColor }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={3.2}>
              <FormControl
                fullWidth
                error={touched.state && Boolean(errors.state)}
                sx={textFieldStyle}
              >
                <Select
                  name='state'
                  value={values.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  //   label='State'
                  displayEmpty
                  renderValue={(selected) =>
                    selected ? (
                      selected
                    ) : (
                      <span style={{ color: '#9e9e9e' }}>State</span>
                    )
                  }
                >
                  {US_STATES.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
                {touched.state && errors.state && (
                  <FormHelperText>{errors.state}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid size={3.3}>
              <TextField
                fullWidth
                placeholder='ZIP'
                name='zipCode'
                value={values.zipCode}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.zipCode && Boolean(errors.zipCode)}
                helperText={touched.zipCode && errors.zipCode}
                sx={{ ...textFieldStyle }}
                inputProps={{
                  maxLength: 7,
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} justifyContent={'center'}>
            <Grid size={6}>
              <Stack direction={'row'} gap={0.5}>
                <MailOutlineIcon
                  fontSize={iconFontSize}
                  sx={{ color: AppColors.autoShowGreenColor }}
                />
                <Typography sx={inputFieldLabelStyle}>
                  Email <span>*</span>
                </Typography>
              </Stack>
              <TextField
                id='email'
                name='email'
                placeholder='Email address'
                type='email'
                fullWidth
                onChange={handleChange}
                value={values.email}
                error={Boolean(errors.email && touched.email)}
                helperText={
                  Boolean(errors.email && touched.email) && errors.email
                }
                sx={{ ...textFieldStyle }}
                inputProps={{
                  maxLength: 50,
                }}
              />
            </Grid>
            <Grid size={6}>
              <Stack direction={'row'} gap={0.5}>
                <PhoneIphoneIcon
                  fontSize={iconFontSize}
                  sx={{ color: AppColors.autoShowGreenColor }}
                />
                <Typography sx={inputFieldLabelStyle}>
                  Mobile Phone <span>*</span>
                </Typography>
              </Stack>
              <TextField
                id='mobileNumber'
                name='mobileNumber'
                placeholder='10-digit mobile #'
                type='tel'
                fullWidth
                onChange={handleChange}
                value={values.mobileNumber}
                error={Boolean(errors.mobileNumber && touched.mobileNumber)}
                helperText={
                  Boolean(errors.mobileNumber && touched.mobileNumber) &&
                  errors.mobileNumber
                }
                sx={{ ...textFieldStyle }}
                inputProps={{
                  maxLength: 10,
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} justifyContent={'center'}>
            <Grid size={6}>
              <Stack direction={'row'} gap={0.5}>
                <CreditCardIcon
                  fontSize={iconFontSize}
                  sx={{ color: AppColors.autoShowGreenColor }}
                />
                <Typography sx={inputFieldLabelStyle}>
                  Last 4 of drivers license <span>*</span>
                </Typography>
              </Stack>
              <TextField
                id='driverLicense'
                name='driverLicense'
                placeholder='Last 4 of drivers license #'
                fullWidth
                onChange={handleChange}
                value={values.driverLicense}
                error={Boolean(errors.driverLicense && touched.driverLicense)}
                helperText={
                  Boolean(errors.driverLicense && touched.driverLicense) &&
                  errors.driverLicense
                }
                sx={{ ...textFieldStyle }}
                inputProps={{
                  maxLength: 4,
                }}
              />
            </Grid>
            <Grid size={6}>
              <Stack direction={'row'} gap={0.5}>
                <InsertInvitationIcon
                  fontSize={iconFontSize}
                  sx={{ color: AppColors.autoShowGreenColor }}
                />
                <Typography sx={inputFieldLabelStyle}>
                  Date of Birth <span>*</span>
                </Typography>
              </Stack>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={
                    values.dateOfBirth ? new Date(values.dateOfBirth) : null
                  }
                  onChange={(value: Date | null) => {
                    setFieldValue('dateOfBirth', value);
                    setFieldTouched('dateOfBirth', true);
                  }}
                  format='MM/dd/yyyy'
                  maxDate={
                    new Date(
                      new Date().getFullYear() - 18,
                      new Date().getMonth(),
                      new Date().getDate(),
                    )
                  }
                  // slots={{ openPickerIcon: () => null }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      name: 'dateOfBirth',
                      error: Boolean(errors.dateOfBirth && touched.dateOfBirth),
                      helperText: Boolean(
                        errors.dateOfBirth && touched.dateOfBirth,
                      )
                        ? errors.dateOfBirth
                        : '',
                      sx: {
                        ...textFieldStyle,
                        '& input': {
                          cursor: 'pointer',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'transparent',
                        },
                        // Override DatePicker-specific padding issues
                        '& .MuiInputBase-input': {
                          paddingTop: '8px !important',
                          paddingBottom: '8px !important',
                        },
                      },
                      inputProps: {
                        maxLength: 10,
                        // readOnly: false, // Allow typing
                      },
                      InputProps: {
                        sx: {
                          maxHeight: 40,
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Stack>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
            mt: 2,
          }}
        >
          <Link
            href={`${process.env.PUBLIC_URL}/pdf/Chance_to_Win_a_Car_A_Day_Terms_And_Conditions.pdf`}
            // href='/privacy-policy'
            target='_blank'
            // underline='none'
            sx={{ color: '#2c3db2', textDecorationColor: 'currentColor' }}
          >
            <Typography variant='body2'>Terms and Conditions</Typography>
          </Link>

          {isSubmitting ? (
            <Loader />
          ) : (
            <BlueGradientButton type='submit'>
              SUBMIT MY ENTRY
            </BlueGradientButton>
          )}

          <Typography variant='caption' textAlign={'center'}>
            Your form is being submitted at {formatDateMMDDYYYY(new Date())}{' '}
            {formatTimeHHmm(new Date())}. Good luck!
          </Typography>
        </Box>
      </form>

      {renderFooter()}
    </>
  );

  const {
    values: questionValues,
    errors: questionErrors,
    touched: questionTouched,
    handleSubmit: handleQuestionSubmit,
    handleChange: handleQuestionChange,
    handleBlur: handleQuestionBlur,
  } = useFormik<UpdateCampaignLead>({
    initialValues: {
      id: campaignLeadData?.campaignLeadId ?? '',
      heardAboutAutoShow: '',
      purchaseVehicleNext6Months: '',
    },
    validationSchema: campaignLeadQuestionsSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!values.id) return;
      try {
        setIsSubmittingQuestions(true);
        await dispatch(updateCampaignLead(values)).unwrap();
        setQuestionsSubmitted(true);
      } catch (error: any) {
        console.error(error);
      } finally {
        setIsSubmittingQuestions(false);
      }
    },
  });

  const renderConfirmation = () => (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundImage: `url('/static/nfada_campaign_page/nfada_banner.png')`,
            backgroundPosition: 'top',
            backgroundSize: '100% auto',
            backgroundRepeat: 'no-repeat',
            gap: 2,
            px: px,
            py: 2,
          }}
        >
          <Box
            component={'img'}
            src='/static/nfada_campaign_page/nfada_logo.png'
            sx={{
              maxHeight: '20vh',
              width: 'auto',
            }}
          />

          {/* <Box
            component={'img'}
            src={confirmationImageSrc}
            alt='Congratulations'
            sx={{
              width: '100%',
              maxWidth: 520,
              height: 'auto',
            }}
          /> */}
          <CheckCircleOutlineIcon
            sx={{ color: AppColors.autoShowGreenColor, fontSize: 70, mt: 2 }}
          />

          <Typography
            variant='h3'
            sx={{ color: AppColors.autoShowGreenColor, fontWeight: 800 }}
          >
            Confirmed!
          </Typography>

          <Typography
            variant='h6'
            sx={{
              borderTop: `1px solid ${AppColors.strokeColor}`,
              borderBottom: `1px solid ${AppColors.strokeColor}`,
              py: 1,
              px: px,
              fontWeight: 600,
              textAlign: 'center',
              color: AppColors.autoShowTextColor
            }}
          >
            Your entry has now been submitted.
          </Typography>

          <Typography
            variant='h6'
            sx={{

              py: 1,
              px: px,
              textAlign: 'center',
            }}
          >
            You will also receive a text message confirmation.
          </Typography>

          {questionsSubmitted ? (
            <Typography variant='body2' fontWeight={600} textAlign='center'>
              Thanks for answering our quick questions!
            </Typography>
          ) : (
            <Box
              component='form'
              onSubmit={handleQuestionSubmit}
              sx={{ width: '100%', maxWidth: 520 }}
              autoComplete='off'
            >
              <Stack spacing={2}>
                <Box>
                  <Typography sx={inputFieldLabelStyle}>
                    How did you hear about the Buffalo Auto Show?
                  </Typography>
                  <FormControl
                    fullWidth
                    error={
                      Boolean(
                        questionTouched.heardAboutAutoShow &&
                        questionErrors.heardAboutAutoShow,
                      )
                    }
                    sx={textFieldStyle}
                  >
                    <Select
                      name='heardAboutAutoShow'
                      value={questionValues.heardAboutAutoShow}
                      onChange={handleQuestionChange}
                      onBlur={handleQuestionBlur}
                      displayEmpty
                      renderValue={(selected) =>
                        selected ? (
                          selected
                        ) : (
                          <span style={{ color: '#9e9e9e' }}>Select one</span>
                        )
                      }
                    >
                      {HEAR_ABOUT_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    {questionTouched.heardAboutAutoShow &&
                      questionErrors.heardAboutAutoShow && (
                        <FormHelperText>
                          {questionErrors.heardAboutAutoShow}
                        </FormHelperText>
                      )}
                  </FormControl>
                </Box>

                <Box>
                  <Typography sx={inputFieldLabelStyle}>
                    Are you planning to purchase a vehicle in the next 6 months?
                  </Typography>
                  <FormControl
                    fullWidth
                    error={
                      Boolean(
                        questionTouched.purchaseVehicleNext6Months &&
                        questionErrors.purchaseVehicleNext6Months,
                      )
                    }
                    sx={textFieldStyle}
                  >
                    <Select
                      name='purchaseVehicleNext6Months'
                      value={questionValues.purchaseVehicleNext6Months}
                      onChange={handleQuestionChange}
                      onBlur={handleQuestionBlur}
                      displayEmpty
                      renderValue={(selected) =>
                        selected ? (
                          selected
                        ) : (
                          <span style={{ color: '#9e9e9e' }}>Select one</span>
                        )
                      }
                    >
                      {PURCHASE_PLAN_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    {questionTouched.purchaseVehicleNext6Months &&
                      questionErrors.purchaseVehicleNext6Months && (
                        <FormHelperText>
                          {questionErrors.purchaseVehicleNext6Months}
                        </FormHelperText>
                      )}
                  </FormControl>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  {isSubmittingQuestions ? (
                    <Loader />
                  ) : (
                    <BlueGradientButton type='submit'>
                      SUBMIT ANSWERS
                    </BlueGradientButton>
                  )}</Box>
              </Stack>
            </Box>
          )}
        </Box>
        <Box sx={{ marginTop: 'auto' }}>{renderFooter()}</Box>
      </Box>
    </>
  );

  return (
    <>
      <Page title={'NFADA CAR-A-DAY'}>
        {isFetchingCampaign && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Loader />
          </Box>
        )}

        {!isFetchingCampaign && !campaignData && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              py: 2,
            }}
          >
            <Typography variant='h3' color='error'>
              Invalid Campaign!!!
            </Typography>
          </Box>
        )}

        {!isFetchingCampaign && campaignData && (
          <Box sx={{ pt: 2 }}>
            {step === 'gate' && renderGateStep()}
            {step === 'entry-form' && renderEntryFormStep()}
            {step === 'confirmation' &&
              campaignLeadData &&
              renderConfirmation()}
          </Box>
        )}
      </Page>
    </>
  );
};

export default NFADACampaign;
